import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Menu, X, Droplets } from 'lucide-react'
import { navigation } from '@data/company'
import { cn } from '@lib/utils'

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location])

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
        isScrolled
          ? 'bg-martimma-dark/90 backdrop-blur-xl border-b border-white/5 py-3'
          : 'bg-transparent py-5'
      )}
    >
      <div className="section-padding max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group" aria-label="Martimma Home">
          <div className="relative w-10 h-10 flex items-center justify-center">
            <Droplets className="w-8 h-8 text-martimma-red group-hover:text-martimma-yellow transition-colors duration-300" />
            <div className="absolute inset-0 bg-martimma-red/20 rounded-full blur-xl group-hover:bg-martimma-yellow/20 transition-colors duration-300" />
          </div>
          <div className="flex flex-col">
            <span className="text-xl font-bold tracking-tight text-white">
              MARTIMMA
            </span>
            <span className="text-[10px] text-martimma-yellow tracking-[0.2em] uppercase -mt-1">
              Global Services
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                'relative px-4 py-2 text-sm font-medium transition-colors duration-300 rounded-full',
                location.pathname === item.href
                  ? 'text-martimma-yellow'
                  : 'text-gray-300 hover:text-white'
              )}
            >
              {item.name}
              {location.pathname === item.href && (
                <motion.div
                  layoutId="activeNav"
                  className="absolute inset-0 bg-martimma-red/10 rounded-full border border-martimma-red/20"
                  transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                />
              )}
            </Link>
          ))}
        </nav>

        {/* CTA Button */}
        <div className="hidden lg:block">
          <Link
            to="/contact"
            className="btn-primary text-sm px-6 py-2.5"
          >
            Get in Touch
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden p-2 text-white"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="lg:hidden bg-martimma-dark/95 backdrop-blur-xl border-t border-white/10"
        >
          <nav className="section-padding py-6 flex flex-col gap-2" aria-label="Mobile navigation">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'px-4 py-3 rounded-xl text-base font-medium transition-colors',
                  location.pathname === item.href
                    ? 'bg-martimma-red/10 text-martimma-yellow border border-martimma-red/20'
                    : 'text-gray-300 hover:bg-white/5 hover:text-white'
                )}
              >
                {item.name}
              </Link>
            ))}
            <Link
              to="/contact"
              className="btn-primary text-center mt-4"
            >
              Get in Touch
            </Link>
          </nav>
        </motion.div>
      )}
    </header>
  )
}
